import React, { ReactNode, useMemo } from 'react';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { ReactComponent as SmsIcon } from '@assets/icons/sms-icon.svg';
import { ReactComponent as AccessIcon } from '@assets/icons/access-icon.svg';
import { ReactComponent as FinancialIcon } from '@assets/icons/financial-icon.svg';
import { ReactComponent as SupportIcon } from '@assets/icons/support-icon.svg';
import { PieChartOutlined } from '@ant-design/icons';
import { RouteItemProps } from './routes/types';
import { useAppRoutes } from './routes/useAppRoutes';

export interface ApplicationCategoriesProps {
  categoryName: string;
  pages: RouteItemProps[];
}

export interface ApplicationsProps {
  type: string;
  name: ApplicationsConstants;
  categories: ApplicationCategoriesProps[];
  icon?: ReactNode;
  alternativeName?: string;
  hasLorawan?: boolean;
}

export const useApps = (): {
  applications: ApplicationsProps[];
} => {
  const { allApplicationRoutes } = useAppRoutes();

  const applications = useMemo(
    () =>
      [
        {
          type: 'product',
          name: ApplicationsConstants.MULTI_CONNECT,
        },
        {
          type: 'product',
          name: ApplicationsConstants.CONNECT_ONE,
        },
        {
          type: 'product',
          name: ApplicationsConstants.MOT_ESSENTIAL,
          alternativeName: 'management',
        },
        {
          type: 'product',
          name: ApplicationsConstants.MOT_ENTERPRISE,
          alternativeName: 'management',
          hasLorawan: true,
        },
        {
          type: 'product',
          name: ApplicationsConstants.MOT_BUSINESS,
          alternativeName: 'management',
          hasLorawan: true,
        },
        {
          type: 'product',
          name: ApplicationsConstants.MOT_PAY,
          alternativeName: 'management',
        },
        {
          type: 'product',
          name: ApplicationsConstants.LORAWAN,
        },
        {
          type: 'product',
          name: ApplicationsConstants.API,
        },
        {
          type: 'app',
          name: ApplicationsConstants.INDICATORS,
          icon: <PieChartOutlined />,
        },
        {
          type: 'app',
          name: ApplicationsConstants.ACCESS,
          icon: <AccessIcon />,
        },
        {
          type: 'app',
          name: ApplicationsConstants.FINANCIAL,
          icon: <FinancialIcon />,
        },
        {
          type: 'app',
          name: ApplicationsConstants.SUPPORT,
          icon: <SupportIcon />,
        },
        {
          type: 'app',
          name: ApplicationsConstants.SMS,
          icon: <SmsIcon />,
        },
      ].map(item => ({
        ...item,
        categories: Object.entries(allApplicationRoutes)
          .map(([categoryName, categoryPages]) => ({
            categoryName,
            pages: categoryPages?.filter(page =>
              page.application?.includes(item.name),
            ),
          }))
          ?.filter(category => category.pages.length > 0),
      })),
    [allApplicationRoutes],
  );

  return {
    applications,
  };
};
